import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import cx from 'classnames';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Patient } from 'stores/patients/patientsTypes';

interface PatientListProps {
  className?: string;
  loading?: boolean;
  patients?: Patient[];
  sortOrder?: 'ascend' | null;
  onClickPatient?: (patientID: string) => void;
  onChangeOrder?: (order: 'ascend' | null) => void;
}

export default function PatientList({
  className = '',
  loading = false,
  patients = [],
  sortOrder = null,
  onClickPatient = () => {},
  onChangeOrder = () => {},
}: PatientListProps) {
  const patientsWithKeys = useMemo(() => patients.map((patient) => ({
    ...patient,
    key: `${patient.idwp}-${patient.firstname}-${patient.lastname}`,
  })), [patients]);
  const onChange = useCallback(
    (_p: any, _f: any, _s: any, { action }: { action: 'sort' | 'filter' | 'paginate' }) => {
      if (action === 'sort') {
        onChangeOrder(sortOrder === null ? 'ascend' : null);
      }
    },
    [onChangeOrder, sortOrder],
  );
  const columns: ColumnsType<Patient> = useMemo(() => [
    {
      key: 'date',
      title: 'RDV',
      dataIndex: 'rdv_3___date',
      render: (date: string) => (
        <span>
          {date && dayjs(date, 'YYYY-MM-DD').endOf('day').isAfter(dayjs().startOf('day')) && (
            <Tag color='volcano'>À venir</Tag>
          )}
          {(!date || !dayjs(date, 'YYYY-MM-DD').endOf('day').isAfter(dayjs().startOf('day'))) && (
            <Tag color='purple'>Passé</Tag>
          )}
        </span>
      ),
      width: 80,
    },
    {
      key: 'date',
      title: 'Date',
      dataIndex: 'rdv_3___date',
      render: (date: string) => date && dayjs(date, 'YYYY-MM-DD').format('D MMMM'),
      width: 120,
      sortDirections: ['ascend'],
      sortOrder,
      sorter: true,
    },
    {
      key: 'hour',
      title: 'Heure',
      dataIndex: 'rdv_3___heure',
      width: 60,
    },
    {
      key: 'name',
      title: 'Nom',
      dataIndex: 'firstname',
      render: (firstname: string, { lastname }: Patient) => (
        <span className='font-medium'>
          {[firstname, lastname].filter((name) => Boolean(name)).join(' ')}
        </span>
      ),
      width: 220,
    },
    // {
    //   key: 'age',
    //   title: 'Age / Date de naissance',
    //   dataIndex: 'date_of_birth',
    //   render: (date: string) => {
    //     const dateOfBirth = dayjs(date, 'DD/MM/YYYY');
    //     const age = Math.floor(dayjs().diff(dateOfBirth, 'years'));

    //     if (!dateOfBirth.isValid()) {
    //       return null;
    //     }

    //     return `${age} an${age > 1 ? 's' : ''} - ${dateOfBirth.format('DD/MM/YYYY')}`;
    //   },
    //   width: 220,
    // },
    {
      key: 'zip',
      title: 'CP',
      dataIndex: 'zip',
    },
    {
      key: 'comments',
      title: 'Commentaires (rendez-vous)',
      dataIndex: 'commentaires_rdv_3',
    },
    {
      key: 'status2',
      title: 'Statut',
      dataIndex: 'rdv_3___statut2',
    },
    {
      key: 'folder',
      title: 'Dossier',
      dataIndex: 'dossier',
      render: (folder: string) => (folder ? (
        <a
          className='underline text-primary'
          href={folder}
          target='_blank'
          rel='noreferrer noopener'
        >
          Lien WeSend
        </a>
      ) : null),
      width: 120,
    },
  ], [sortOrder]);
  const onRow = useCallback(
    (record: Patient) => ({
      onClick: () => {
        onClickPatient(record.idwp);
      },
      onDoubleClick: () => {},
      onContextMenu: () => {},
      onMouseEnter: () => {},
      onMouseLeave: () => {},
    }),
    [onClickPatient],
  );
  const rowClassName = useCallback(
    (record: Patient, index: number) => {
      if (
        index < patientsWithKeys.length - 1
        && record.rdv_3___date
        && patientsWithKeys[index + 1]?.rdv_3___date
      ) {
        const now = dayjs().startOf('day');
        const recordDate = dayjs(record.rdv_3___date).endOf('day');

        if (recordDate.isAfter(now)) {
          const nextDate = dayjs(patientsWithKeys[index + 1].rdv_3___date).endOf('day');

          if (nextDate.isBefore(now)) {
            return cx(
              'cursor-pointer relative',
              'after:absolute after:w-full after:h-[2px]',
              'after:bg-primary after:bottom-0 after:left-0',
            );
          }
        }
      }

      return 'cursor-pointer';
    },
    [patientsWithKeys],
  );

  return (
    <Table
      onChange={onChange}
      rowClassName={rowClassName}
      pagination={false}
      className={className}
      dataSource={patientsWithKeys}
      columns={columns}
      loading={loading}
      onRow={onRow}
      size='small'
      bordered
    />
  );
}
